import { toast } from 'react-toastify';

export interface ErrorType {
  response: { data: ErrorResponse };
}

export interface ErrorResponse {
  message?: string;
  details?: string;
}

/**
 * Backend messages lead with an error code such as "[12200-1]" so support can
 * trace them in the logs. The code means nothing to the person reading the
 * toast, so it is dropped before display. Code that branches on an error code
 * reads it from the raw response, which still has it.
 */
export function withoutErrorCode<T>(message: T): T {
  return (typeof message === 'string' ? message.replace(/\[\d+-\d+\]\s*/g, '').trim() : message) as T;
}

function responseDataMessage(data: unknown): string {
  if (data == null) return '';
  if (typeof data === 'string') return data;
  if (typeof data === 'object' && 'message' in data && typeof (data as { message: unknown }).message === 'string') {
    return (data as { message: string }).message;
  }
  return '';
}

/** Backend validation / JSON parse failures often use these codes or messages. */
export function isJsonInvalidApiError(data: unknown): boolean {
  if (data == null || typeof data !== 'object') return false;
  const o = data as Record<string, unknown>;
  const code = String(o.code ?? o.error ?? o.type ?? '').toLowerCase();
  const msg = String(o.message ?? o.details ?? '').toLowerCase();
  if (code === 'json_invalid' || code.includes('json_invalid')) return true;
  if (msg.includes('json_invalid') || msg.includes('invalid json') || msg.includes('malformed json')) return true;
  return false;
}

/** PATCH /collector/users/:id — clearer errors for JSON/403/404. */
export function displayCollectorUserPatchError(error: unknown): void {
  const ax = error as {
    response?: { status?: number; data?: unknown };
    code?: string;
    message?: string;
  };
  const status = ax.response?.status;
  const data = ax.response?.data;

  if (data && isJsonInvalidApiError(data)) {
    toast.error('Invalid request payload.', { className: 'fs-unmask' });
    return;
  }

  if (status === 403) {
    toast.error(withoutErrorCode(responseDataMessage(data)) || 'You do not have access to this resource.', {
      className: 'fs-unmask',
    });
    return;
  }
  if (status === 404) {
    toast.error(withoutErrorCode(responseDataMessage(data)) || 'User/record not found or not accessible.', {
      className: 'fs-unmask',
    });
    return;
  }

  displayErrorDetails(error);
}

export function tryDisplayHttpResourceError(error: unknown): boolean {
  const toastConfig = { className: 'fs-unmask' };
  const ax = error as { response?: { status?: number; data?: unknown } };
  const status = ax.response?.status;
  const data = ax.response?.data;

  if (status === 403) {
    toast.error(withoutErrorCode(responseDataMessage(data)) || 'You do not have access to this resource.', toastConfig);
    return true;
  }
  if (status === 404) {
    toast.error(withoutErrorCode(responseDataMessage(data)) || 'User/record not found or not accessible.', toastConfig);
    return true;
  }
  if (status === 422 || (status === 400 && isJsonInvalidApiError(data))) {
    toast.error(withoutErrorCode(responseDataMessage(data)) || 'Invalid request payload.', toastConfig);
    return true;
  }
  if (data && isJsonInvalidApiError(data)) {
    toast.error('Invalid request payload.', toastConfig);
    return true;
  }
  return false;
}

export function displayErrorDetails(error: any, networkErrorMessage?: string): void {
  if (
    networkErrorMessage &&
    (error?.code === 'ERR_NETWORK' ||
      error?.message === 'Network Error' ||
      error === 'ERR_NETWORK' ||
      error === 'Network Error')
  ) {
    toast.error(networkErrorMessage, { className: 'fs-unmask' });
    return;
  }

  if (tryDisplayHttpResourceError(error)) {
    return;
  }
  if (
    (error as ErrorType).response &&
    (error as ErrorType).response.data &&
    error.code !== 'ERR_NETWORK' &&
    error.message !== 'Network Error'
  ) {
    const { response } = error as ErrorType;
    if (response.data.message && response.data.details) {
      toast.error(withoutErrorCode(`${response.data.message} \n${response.data.details}`), { className: 'fs-unmask' });
    } else if (response.data.details) {
      toast.error(withoutErrorCode(response.data.details), { className: 'fs-unmask' });
    } else {
      toast.error(withoutErrorCode(response.data.message), { className: 'fs-unmask' });
    }
  } else if (typeof error === 'string' && error !== 'ERR_NETWORK' && error !== 'Network Error') {
    toast.error(withoutErrorCode(error), { className: 'fs-unmask' });
  } else if (error.code !== 'ERR_NETWORK' && error.message !== 'Network Error') {
    toast.error(withoutErrorCode((error as Error).message), { className: 'fs-unmask' });
  }
}
